import type { components } from '~/generated/types.d.ts'

/**
 * Formatted credit string from an array of credits
 *
 * @param credits - Objects containing the artist id and an optional joinphrase
 * @param artists - Normalized artist objects
 */
export const generateTrackCreditString = (
  credits: components['schemas']['ArtistCredit'][],
  artists: components['schemas']['ArtistWithAlbums'][]
): string =>
  Array.from(
    // Experimental point-free use of a generator - discuss this pattern; it suits our architecture patterns well but we haven't used it yet so far and it's not yet so common outside of libraries
    (function* () {
      for (const credit of credits) {
        const artist = artists.find(artist => artist.id === credit.artist.id)
        if (!artist) continue
        yield `${artist.name}${credit.joinphrase ? ` ${credit.joinphrase}` : ''}`
      }
    })()
  ).join('')

/**
 * Cover URL path from a single artist
 *
 * @param artist - Normalized artist object containing a cover field
 * @param field - Size/crop string of the cover URL
 * @param resolveUrl - Pure function to transform the relative path into an absolute URL
 */
export const getArtistCoverUrl = (
  artist: components['schemas']['ArtistWithAlbums'],
  field: keyof components["schemas"]["CoverField"]['urls'],
  resolveUrl: (path: string) => string
): string | null =>
  (path => (path ? resolveUrl(path) : null))(artist.cover?.urls?.[field])

/**
 * First available artist cover from a list of artists and resolves its URL
 *
 * @param artists - Array of normalized artist objects
 * @param field - Size/crop string of the cover URL
 * @param resolveUrl - Pure function to transform the relative path into an absolute URL
 */
export const getFirstArtistCoverUrl = (
  artists: components['schemas']['ArtistWithAlbums'][],
  field: keyof components["schemas"]["CoverField"]['urls'],
  resolveUrl: (path: string) => string
): string | null =>
  (url => (url ? resolveUrl(url) : null))(
    artists.map(artist => artist.cover?.urls?.[field]).find(Boolean)
  )
